import clsx from "clsx";
import React from "react";
import { DndContext } from "react-dnd";
import {
  type CursorProps,
  type MoveHandler,
  type NodeRendererProps as TreeNodeRendererProps,
  type RowRendererProps,
  Tree as ArboristTree,
  TreeApi,
} from "react-arborist";
import { createPluggable } from "pluggable-react";
import Item, { type TreeItemProps, type TreeNode } from "./TreeItem";
import styles from "./Tree.module.scss";

export { TreeItemProps, TreeApi, TreeNodeRendererProps };

export interface TreeContextValue<TInfo = unknown> {
  filterItems: (children: TreeNode<TInfo>[]) => TreeNode<TInfo>[];
  showIndentBorders?: boolean;
  focusSelected?: boolean;
  controlledExpandedState?: Record<string, boolean | undefined> | null;
  openByDefault?: boolean;
  toggleExpanded?: (nodeId: string, isOpen: boolean) => void;
}

export const TreeContext = React.createContext<TreeContextValue<any> | null>(null);

export interface TreeNodeData<TInfo = unknown> {
  id: string;
  label: React.ReactNode;
  info?: TInfo;
  children?: TreeNodeData<TInfo>[];
  selectable?: boolean;
}

export interface TreeProps<TInfo = unknown> {
  data: TreeNodeData<TInfo>[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  className?: string;
  indent?: number;
  rowHeight?: number;
  initialOpenState?: Record<string, boolean>;
  children?: (props: TreeItemProps<TInfo>) => React.ReactNode;
  searchTerm?: string;
  openByDefault?: boolean;
  expandedState?: Record<string, boolean | undefined>;
  onExpandedStateChange?: (expandedState: Record<string, boolean | undefined>) => void;
  onSearchItemMatch?: (node: TreeNode<TInfo>, searchTerm: string) => boolean;
  showIndentBorders?: boolean;
  focusSelected?: boolean;
  dnd?: boolean;
  onMove?: MoveHandler<TreeNodeData<TInfo>>;
  disableDrop?: boolean;
  disableDrag?: boolean;
  overscanCount?: number;
  handleRef?: React.MutableRefObject<TreeApi<TreeNodeData<TInfo>> | null>;
}

const TreeCursor = React.memo(function TreeCursor({ top, left, indent }: CursorProps) {
  return (
    <div
      style={{
        position: "absolute",
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
        zIndex: 1,
        top: `${top - 2}px`,
        left: `${left}px`,
        right: `${indent}px`,
      }}
    >
      <div
        style={{
          width: "4px",
          height: "4px",
          boxShadow: "0 0 0 3px var(--primary)",
          borderRadius: "50%",
        }}
      />
      <div
        style={{
          flex: 1,
          height: "2px",
          background: "var(--primary)",
          borderRadius: "1px",
        }}
      />
    </div>
  );
});

const PassiveTreeRow = <TInfo = unknown,>({ attrs, innerRef, children }: RowRendererProps<TreeNodeData<TInfo>>) => (
  <div
    {...attrs}
    ref={innerRef}
    onFocus={(e) => e.stopPropagation()}
  >
    {children}
  </div>
);

const getNodeOpenState = <TInfo = unknown,>(
  node: TreeNodeData<TInfo>,
  expandedState: Record<string, boolean | undefined> | undefined,
  openByDefault: boolean | undefined,
) => {
  const controlledOpen = expandedState?.[String(node.id)];
  return typeof controlledOpen === "boolean" ? controlledOpen : Boolean(openByDefault);
};

const resolveControlledOpenState = <TInfo = unknown,>(
  nodes: TreeNodeData<TInfo>[],
  expandedState: Record<string, boolean | undefined> | undefined,
  openByDefault: boolean | undefined,
) => {
  const initialOpenState: Record<string, boolean> = {};
  const visit = (node: TreeNodeData<TInfo>) => {
    if (node.children?.length) {
      initialOpenState[String(node.id)] = getNodeOpenState(node, expandedState, openByDefault);
      for (const child of node.children) visit(child);
    }
  };

  for (const node of nodes) visit(node);
  return initialOpenState;
};

const DefaultTree = <TInfo = unknown>(props: TreeProps<TInfo>) => {
  const {
    data, children, selectedId, onSelect, className,
    indent = 24, rowHeight = 32, initialOpenState, searchTerm, onSearchItemMatch, openByDefault,
    showIndentBorders = true, focusSelected = true,
    dnd = false, onMove, disableDrop, disableDrag, overscanCount, handleRef, expandedState, onExpandedStateChange,
  } = props;
  const isExpansionControlled = typeof expandedState !== "undefined";
  const controlledInitialOpenState = React.useMemo<Record<string, boolean> | undefined>(() => {
    if (!isExpansionControlled) return undefined;
    return resolveControlledOpenState(data, expandedState, openByDefault);
  }, [isExpansionControlled, data, expandedState, openByDefault]);
  const treeKey = React.useMemo(() => {
    if (!isExpansionControlled) return "tree-uncontrolled";
    const entries = Object.entries(expandedState ?? {})
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}:${String(v)}`)
      .join("|");
    return `tree-controlled-${Boolean(openByDefault)}-${entries}`;
  }, [isExpansionControlled, expandedState, openByDefault]);
  const toggleExpanded = React.useCallback((nodeId: string, isOpen: boolean) => {
    if (!isExpansionControlled || !onExpandedStateChange) return;
    const next: Record<string, boolean | undefined> = { ...(expandedState ?? {}) };
    next[nodeId] = !isOpen;
    onExpandedStateChange(next);
  }, [isExpansionControlled, onExpandedStateChange, expandedState]);

  const treeData = React.useMemo(() => data, [ data ]);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const dndContext = React.useContext(DndContext);
  const [ size, setSize ] = React.useState({ width: 300, height: 300 });

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        setSize({ width: Math.max(100, cr.width), height: Math.max(100, cr.height) });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const internalApiRef = React.useRef<TreeApi<TreeNodeData<TInfo>> | null>(null);

  React.useEffect(() => {
    if (!handleRef) return;
    handleRef.current = internalApiRef.current;
    return () => {
      handleRef.current = null;
    };
  }, [handleRef, treeData]);

  const renderItem = (props: TreeNodeRendererProps<TreeNodeData<TInfo>>) => {
    const Component = (children ?? Item) as any;
    return (
      <Component
        isActive={selectedId === String(props.node.data.id)}
        onSelect={onSelect}
        focusSelected={focusSelected}
        {...props}
      />
    );
  };

  const filterItems = (items: TreeNode<TInfo>[]) => {
    if (!searchTerm) return items;
    return items.filter((child) => onSearchItemMatch?.(child, searchTerm));
  };

  return (
    <TreeContext.Provider
      value={{ filterItems, showIndentBorders, focusSelected, controlledExpandedState: expandedState ?? null, openByDefault, toggleExpanded }}
    >
      <div
        data-deci-tree="true"
        ref={containerRef}
        className={clsx(styles.tree, className)}
      >
        <ArboristTree
          key={treeKey}
          ref={internalApiRef as any}
          selectionFollowsFocus={false}
          disableMultiSelection={true}
          data={treeData as any}
          width={size.width}
          height={size.height}
          indent={indent}
          initialOpenState={isExpansionControlled ? (controlledInitialOpenState ?? {}) : (initialOpenState ?? {})}
          rowHeight={rowHeight}
          overscanCount={overscanCount}
          selection={focusSelected ? selectedId || undefined : undefined}
          openByDefault={isExpansionControlled ? false : openByDefault}
          searchTerm={searchTerm}
          searchMatch={onSearchItemMatch}
          renderRow={focusSelected ? undefined : PassiveTreeRow}
          renderCursor={TreeCursor}
          onMove={onMove as MoveHandler<any> | undefined}
          disableDrag={dnd ? disableDrag : true}
          disableDrop={dnd ? disableDrop : true}
          dndManager={dndContext?.dragDropManager}
        >
          {renderItem}
        </ArboristTree>
      </div>
    </TreeContext.Provider>
  );
};

const Tree = createPluggable<TreeProps<any>>("deci.tree", DefaultTree);

export default Object.assign(Tree, { Item });
